"use client";

import { useIsPresent } from "framer-motion";
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./FirstRunIntroduction.module.css";

type Stage = "introduce" | "explain" | "question" | "complete";
// The first Hello finishes at 550ms; let its full stop breathe for 400ms.
const CAPTION_START_MS = 550 + 400;
const STEPS: readonly [number, Stage][] = [
  [CAPTION_START_MS + 1550, "explain"],
  [CAPTION_START_MS + 4800, "question"],
  [CAPTION_START_MS + 5850, "complete"],
];
const NAME = "Chicky";
const GLYPHS = "こんエこにソ";
const NAME_CHARACTER_MS = 45;
const INTRO_REVEALS = [
  ...Array.from("I'm", (_, index) => CAPTION_START_MS + index * NAME_CHARACTER_MS),
  ...Array.from(NAME, (_, index) => CAPTION_START_MS + 200 + index * NAME_CHARACTER_MS),
];

function StreamedText({ text, revealed }: { text: string; revealed?: number }) {
  return Array.from(text, (character, index) => (
    <span
      key={index}
      className={styles.character}
      data-typed={revealed === undefined ? undefined : index < revealed}
      style={{ "--character-delay": `${index * 25}ms` } as CSSProperties}
    >
      {character}
    </span>
  ));
}

export function FirstRunIntroduction({
  reduce,
  className,
  onComplete,
}: {
  reduce: boolean;
  className: string;
  onComplete: () => void;
}) {
  const [stage, setStage] = useState<Stage>(reduce ? "complete" : "introduce");
  const [revealed, setRevealed] = useState(reduce ? INTRO_REVEALS.length : 0);
  const [hovered, setHovered] = useState(false);
  const present = useIsPresent();
  const finished = useRef(false);
  const sentenceRef = useRef<HTMLSpanElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!present) return;
    if (reduce || finished.current) {
      finished.current = true;
      setStage("complete");
      setRevealed(INTRO_REVEALS.length);
      onComplete();
      return;
    }
    const timers = [
      ...INTRO_REVEALS.map((at, index) => window.setTimeout(() => setRevealed(index + 1), at)),
      ...STEPS.map(([at, next]) =>
        window.setTimeout(() => {
          setStage(next);
          if (next === "complete") {
            finished.current = true;
            onComplete();
          }
        }, at)
      ),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [onComplete, present, reduce]);

  // The invisible slot reserves the final sentence from first paint. Measure its
  // real centre so the subject glides into place with any font or viewport.
  useLayoutEffect(() => {
    const sentence = sentenceRef.current;
    const anchor = anchorRef.current;
    const suffix = suffixRef.current;
    if (!sentence || !anchor || !suffix) return;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const currentFit = Number(sentence.style.getPropertyValue("--sentence-fit")) || 1;
      const gap = suffix.offsetLeft - anchor.offsetLeft - anchor.offsetWidth;
      const natural = (anchor.offsetWidth + gap + suffix.offsetWidth) / currentFit;
      if (natural > 0 && sentence.clientWidth > 0) {
        sentence.style.setProperty(
          "--sentence-fit",
          String(Math.min(1, (sentence.clientWidth - 1) / natural))
        );
      }
      const letters = Array.from(
        nameRef.current?.querySelectorAll<HTMLElement>("[data-latin-letter]") ?? []
      );
      const widths = letters.map((letter) => letter.getBoundingClientRect().width);
      const glyphSlot = widths.reduce((sum, width) => sum + width, 0) / GLYPHS.length;
      let offset = 0;
      letters.forEach((letter, index) => {
        const cell = letter.parentElement;
        const glyph = letter.nextElementSibling as HTMLElement | null;
        cell?.style.setProperty("--latin-width", `${widths[index]}px`);
        if (glyphSlot > 0 && glyph && glyph.offsetWidth > 0) {
          // Fit six evenly spaced glyphs into the natural English word width,
          // so hover never pushes the comma or the following tutor sentence.
          cell?.style.setProperty("--glyph-left", `${glyphSlot * (index + 0.5) - offset}px`);
          cell?.style.setProperty(
            "--glyph-scale",
            String(Math.min(1, glyphSlot / glyph.offsetWidth))
          );
        }
        offset += widths[index];
      });
      sentence.style.setProperty(
        "--subject-final-x",
        `${anchor.offsetLeft + anchor.offsetWidth / 2}px`
      );
    };
    measure();
    void document.fonts?.ready.then(measure);
    if (typeof ResizeObserver === "undefined")
      return () => {
        disposed = true;
      };
    const observer = new ResizeObserver(measure);
    observer.observe(sentence);
    observer.observe(anchor);
    return () => {
      disposed = true;
      observer.disconnect();
    };
  }, []);

  const current = reduce ? "complete" : stage;
  const expanded = current === "explain" || current === "question" || current === "complete";
  const question = current === "question" || current === "complete";

  return (
    <h1 className={className}>
      <span className="sr-only">
        I&apos;m Chicky, your AI language tutor. Which language shall we speak?
      </span>
      <span
        aria-hidden="true"
        className={styles.content}
        data-introduction-stage={current}
        data-expanded={expanded}
        data-question={question}
      >
        <span ref={sentenceRef} className={styles.sentence}>
          <span ref={anchorRef} className={styles.anchor}>
            I&apos;m <span className={styles.nameSlot}>Chicky</span>,
          </span>{" "}
          <span ref={suffixRef} className={styles.suffix}>
            <StreamedText text="your AI language tutor." />
          </span>
          <span className={styles.subject}>
            <span className={styles.opening}>
              <StreamedText text="I'm" revealed={revealed} />
            </span>{" "}
            <span
              ref={nameRef}
              className={styles.name}
              data-name-morph={present && !reduce && current !== "introduce" && hovered}
              onPointerEnter={(event) => {
                if (event.pointerType !== "touch") setHovered(true);
              }}
              onPointerLeave={() => setHovered(false)}
            >
              {Array.from(NAME, (letter, index) => (
                <span
                  key={index}
                  className={styles.letter}
                  style={{ "--letter-delay": `${index * 60}ms` } as CSSProperties}
                >
                  <span
                    className={styles.latin}
                    data-latin-letter
                    data-typed={reduce || revealed > index + 3}
                  >
                    {letter}
                  </span>
                  <span className={styles.glyph}>{GLYPHS[index]}</span>
                </span>
              ))}
            </span>
            <span className={styles.comma}>,</span>
          </span>
        </span>
        <span className={styles.question}>
          <StreamedText text="Which language shall we speak?" />
        </span>
      </span>
    </h1>
  );
}
